import {
    CreateUserRequest,
    GetUserResponse,
    GetAgencyUserResponse,
    CreateInternalUserRequest,
    UpdateUserRequest,
    EditRoles,
    IdentificationType
} from 'Application/models/';
import {
    createUser,
    fetchAgencyUsers,
    getUsers,
    getCustomerDetails,
    handleActivateUsers,
    handleDeactivateUsers,
    handleEditUsers,
    fetchInternalUsers,
    createInternalUser,
    handleEditUserRole
} from 'Application/service';
import { create } from 'zustand';
import { CreateUserState, GetUserState, GetCustomerDetailsState } from 'Application/models';
import { convertToEnumKey } from 'Application/utils';

// Merging CreateUserState, GetUserState, and GetCustomerDetailsState into one store
export const useCreateUserStore = create<CreateUserState & GetUserState & GetCustomerDetailsState>((set) => ({
    // Common state
    isLoading: false,
    isDownloadFileLoading: false,
    isAgencyUsersLoading: false,
    isError: false,
    error: null,
    userData: null,
    isEditUserLoading: false,

    // GetUserState properties
    getUserData: null,
    agencyUserData: null,
    editUserData: null,
    editUserRoleData: null,
    activateUserData: null,
    deactivateUserData: null,

    // GetCustomerDetailsState properties
    customerDetails: null,

    // CreateUser method
    createUser: async (userData: CreateUserRequest, name: string) => {
        set({ isLoading: true });
        try {
            await createUser(userData);
            set({ isError: false, error: null });
            await useCreateUserStore.getState().getUserList(); // Refresh users list after creating
            await useCreateUserStore.getState().getAgecyUsersList(name); // Refresh users list after creating
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    // CreateUser method
    createInternalUser: async (userData: CreateInternalUserRequest) => {
        set({ isLoading: true });
        try {
            await createInternalUser(userData);
            set({ isError: false, error: null });
            await useCreateUserStore.getState().getUserList(); // Refresh users list after creating
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
            set({ isError: true, error: errorMessage });
            throw errorMessage; // Throw errorMessage for the component to handle
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch user list
    getUserList: async (searchValue?: string, page?: number, pageSize?: number) => {
        set({ isLoading: true });
        try {
            const response: GetUserResponse = await getUsers(searchValue, page, pageSize);
            set({ isError: false, error: null, getUserData: response });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch agency users
    getAgecyUsersList: async (name: string) => {
        set({ isAgencyUsersLoading: true });
        try {
            const response: GetAgencyUserResponse = await fetchAgencyUsers(name);
            set({ isError: false, error: null, agencyUserData: response });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isAgencyUsersLoading: false });
        }
    },

    // Fetch britam users
    getInternalUsers: async (searchValue?: string, page?: number, pageSize?: number, countryValue?: string) => {
        set({ isLoading: true });
        try {
            const response: GetAgencyUserResponse = await fetchInternalUsers(searchValue, page, pageSize, countryValue);
            set({ isError: false, error: null, agencyUserData: response });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    // Fetch customer details
    getCustomerDetails: async (identificationNumber: string, identificationType: IdentificationType = IdentificationType.PASSPORT) => {
        set({ isLoading: true });
        try {
            const response = await getCustomerDetails(identificationNumber, convertToEnumKey(identificationType));

            set({ isError: false, error: null, customerDetails: response });

            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    // Activate user
    activateUser: async (emailAddress: string) => {
        set({ isLoading: true });
        try {
            const response = await handleActivateUsers(emailAddress);
            set({ isError: false, error: null, activateUserData: response });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    deactivateUser: async (emailAddress: string) => {
        set({ isLoading: true });

        try {
            const response = await handleDeactivateUsers(emailAddress);
            set({ isError: false, error: null, deactivateUserData: response }); // Fix: set deactivateUserData here
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    editUser: async (userId: number, userData: UpdateUserRequest) => {
        set({ isLoading: true });

        try {
            const response = await handleEditUsers(userId, userData);
            set({ isError: false, error: null, editUserData: response });
            // console.log('agencyName', agencyName)
            // await useCreateUserStore.getState().getInternalUsers();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    editUserRole: async (userId: number, userRole: EditRoles) => {
        set({ isLoading: true });
        try {
            const response = await handleEditUserRole(userId, userRole);
            set({ isError: false, error: null, editUserRoleData: response });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ isError: true, error: errorMessage });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    }
}));
